/* Reorder accordion — one card open at a time, plus the already-subscribed check.
   Paired with snippets/llm-account-reorder-card.liquid and assets/llm-account-reorder.css.

   ── WHY THE SUBSCRIPTION CHECK IS LAZY ──
   "Is this product already on Autoship?" cannot be answered in Liquid: Appstle contracts are not
   on the `customer` object. The only source is Appstle's own app proxy, which the Autoship portal
   already uses (templates/page.autoship.liquid:103):

       GET /apps/subscriptions/cp/api/subscription-customers-detail/valid/{customerId}  -> contracts
       GET /apps/subscriptions/cp/api/subscription-contracts/contract/{id}              -> line items

   That is 1+N requests, and this band renders on the HOMEPAGE for every signed-in customer. Doing
   it on page load would spend those requests on everyone, including the majority who never open a
   card. The accordion makes it free: the Autoship tier is invisible until someone clicks, so we
   fetch on FIRST EXPAND and cache for the page. No new worker endpoint needed — same-origin app
   proxy with credentials.

   ⛔ THE FETCH WINDOW IS A CORRECTNESS WINDOW, NOT A COSMETIC ONE.
   Cards render as data-ro-as="loading" and MUST NOT be promoted to "offer" until the check
   resolves. A customer who already subscribes, pressing "Start Autoship" before we know, creates a
   SECOND contract for the same product — double billing and a support ticket. On FAILURE we stay
   restrictive and fall back to "offer" only after saying so is impossible... see onFail() below:
   we show the manage link instead of a Start button, because a missed upsell is cheap and a
   duplicate subscription is not.

   ⚠️ Shopify handles `selling_plan` natively on /cart/add.js, so subscribing here is just an
   add-to-cart carrying a plan id — checkout creates the contract. There is no Appstle write call,
   which is why this does not hit the int64-vs-GID trap that broke the frequency update (task #93). */
(function () {
  'use strict';

  var API = '/apps/subscriptions/cp/api';

  /* One promise for the whole page. Every card awaits the same fetch, so opening four cards costs
     one round of requests, not four. Cached as the PROMISE (not the result) so concurrent opens
     during the first flight all join it rather than each starting their own. */
  var subsPromise = null;

  function customerId() {
    var el = document.querySelector('[data-llm-ro-customer]');
    return el ? el.getAttribute('data-llm-ro-customer') : '';
  }

  function api(path) {
    return fetch(API + path, {
      headers: { Accept: 'application/json' },
      credentials: 'include'
    }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /* Resolves to a Map of productId(string) -> frequency label, for ACTIVE contracts only.
     Keyed on product id rather than variant: subscribing to a different SIZE of something you
     already autoship is still a duplicate subscription to the customer's eye, and the owner's
     concern is the duplicate contract, not the exact variant. */
  function loadSubscriptions() {
    if (subsPromise) return subsPromise;

    var cid = customerId();
    if (!cid) {
      subsPromise = Promise.reject(new Error('no customer id'));
      return subsPromise;
    }

    subsPromise = api('/subscription-customers-detail/valid/' + encodeURIComponent(cid))
      .then(function (data) {
        var contracts = Array.isArray(data) ? data : [];
        var map = new Map();

        contracts.forEach(function (c) {
          /* ⚠️ Only live contracts count. A CANCELLED contract must NOT suppress the offer — that
             customer is precisely who we want back, and telling them they are "already on
             Autoship" for something they cancelled would be both wrong and insulting. PAUSED does
             count: the contract still exists, so starting another really would duplicate it.
             The API's own value is lower-case and BRITISH ("cancelled") — see statusOf/STATUS_LABEL
             in page.autoship.liquid:384-392. Uppercased here only to be spelling-agnostic. */
          var s = String(c.status || '').toUpperCase();
          if (s !== 'ACTIVE' && s !== 'PAUSED') return;

          var freq = freqLabel(c);
          linesOf(c).forEach(function (li) {
            var key = productIdOf(li);
            if (key && !map.has(key)) map.set(key, freq);
          });
        });

        return map;
      });

    return subsPromise;
  }

  /* ⚠️ THE LINE ITEMS ARE ON THE LIST PAYLOAD, AS A JSON *STRING*.
     Lifted from page.autoship.liquid:400-404 (linesOf) rather than guessed. This is the whole
     reason the check costs ONE request: an earlier draft of this file read `detail.lines` from a
     per-contract call, which (a) would have returned [] for every contract, silently producing an
     EMPTY map — i.e. "nobody is subscribed", re-opening the duplicate-subscription hole the
     loading state exists to close — and (b) would have cost 1+N requests to get nothing.
     ⛔ Do not "simplify" this to c.lines or a detail call. The field is contractDetailsJSON. */
  function linesOf(c) {
    var d = c.contractDetailsJSON;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { return []; } }
    return Array.isArray(d) ? d : [];
  }

  /* Appstle's contract payload carries a numeric productId (sometimes GID-shaped), and no handle.
     Same normalisation as page.autoship.liquid:582-584 so this joins against Liquid's
     numeric product.id. */
  function productIdOf(li) {
    return String((li && li.productId) || '').split('/').pop();
  }

  /* "· every 4 weeks". Field names taken from the portal's own every() helper
     (page.autoship.liquid:377-382), not guessed. Returns '' when either part is missing so the
     copy degrades to a bare "Already on Autoship" — still true, still useful.
     ⛔ Never invent a frequency: a wrong interval here would read as a statement about what the
     customer is being billed, which they would reasonably act on. */
  function freqLabel(c) {
    var n = Number(c.billingPolicyIntervalCount) || 0;
    var unit = String(c.billingPolicyInterval || '').toLowerCase();
    if (!n || !unit) return '';
    var u = unit.replace(/s$/, '');
    /* ⚠️ NO leading ' · ' separator. This is now its OWN line under "Already on Autoship" (owner
       2026-10-20), not a continuation of it, and the card hides the element when it is empty — a
       bare bullet with nothing after it would be worse than omitting the interval. */
    return 'every ' + (n === 1 ? '' : n + ' ') + u + (n === 1 ? '' : 's');
  }

  function setState(as, state) {
    as.setAttribute('data-ro-as', state);
  }

  /* Resolve one card's Autoship tier. Called on first expand only. */
  function resolveAutoship(card) {
    var as = card.querySelector('[data-llm-ro-as]');
    if (!as || as.getAttribute('data-ro-resolved') === '1') return;
    as.setAttribute('data-ro-resolved', '1');

    loadSubscriptions()
      .then(function (map) {
        var pid = card.getAttribute('data-ro-product');
        if (map.has(pid)) {
          var slot = as.querySelector('[data-llm-ro-havefreq]');
          if (slot) slot.textContent = map.get(pid) || '';
          setState(as, 'have');
        } else {
          setState(as, 'offer');
        }
      })
      .catch(function (err) {
        console.warn('[reorder] subscription check failed', err);
        /* ⛔ DO NOT fall back to "offer" here. We do not know whether they are subscribed, and
           guessing "not subscribed" is the guess that can create a duplicate contract. "have"
           without a frequency reads as "Already on Autoship" + a manage link, which is a dead end
           for a non-subscriber (mildly wrong, costs an upsell) but cannot double-bill anyone.
           A missed upsell is cheap; a duplicate subscription is not. */
        setState(as, 'have');
      });
  }

  function close(card) {
    var body = card.querySelector('[id]');
    var btn = card.querySelector('[data-llm-ro-toggle]');
    if (!body || !btn) return;
    body.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    card.classList.remove('llm-ro--open');
  }

  function open(card) {
    var btn = card.querySelector('[data-llm-ro-toggle]');
    if (!btn) return;
    var body = document.getElementById(btn.getAttribute('aria-controls'));
    if (!body) return;

    /* One at a time — the owner's "shrink the active card" ask. Scoped to the card's OWN list so
       two reorder lists on one page (overview + homepage band) do not close each other's cards. */
    var list = card.closest('[data-llm-ro-list]') || document;
    list.querySelectorAll('[data-llm-ro]').forEach(function (other) {
      if (other !== card) close(other);
    });

    body.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    card.classList.add('llm-ro--open');
    resolveAutoship(card);
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var toggle = e.target.closest('[data-llm-ro-toggle]');
    if (toggle) {
      e.preventDefault();
      var card = toggle.closest('[data-llm-ro]');
      if (!card) return;
      if (toggle.getAttribute('aria-expanded') === 'true') close(card);
      else open(card);
      return;
    }

    /* "Start Autoship" needs NO handler here. The button carries data-plan-from="<select id>" and
       llm-account-cart.js reads that select's value at click time, so the subscribe path is the
       same single add-to-cart implementation as everything else on the account pages.
       ⛔ Deliberately not stamping a data-selling-plan from this file: both files listen on
       `document`, so handler order is decided by script load order, and losing that race would
       charge a one-time purchase to someone who thought they subscribed. */
  });

  /* ── CHANGING THE VARIANT ───────────────────────────────────────────────────
     Owner 2026-10-20. Four things must move together, and missing any one of them ships a card
     that LOOKS right and buys the wrong thing:

       1. data-add on the one-time button
       2. data-add on the "Add to Autoship" button   ← the easy one to forget. Edit the size, then
          subscribe, and without this you are billed forever for the OLD size.
       3. the displayed price and variant name in the collapsed head
       4. the frequency dropdown — selling-plan allocations are PER VARIANT, so a different size
          can offer different intervals, or none

     The already-subscribed map is keyed on PRODUCT id, so it does not need recomputing here —
     subscribing to a second size of something you already autoship is still a duplicate. */
  function applyVariant(card, opt) {
    if (!card || !opt) return;

    var id = opt.value;
    card.setAttribute('data-ro-variant', id);
    card.querySelectorAll('[data-add]').forEach(function (btn) {
      btn.setAttribute('data-add', id);
    });

    /* Pre-formatted by Liquid — never re-derive a currency string here. */
    var priceEl = card.querySelector('.llm-ro__price');
    var price = opt.getAttribute('data-price');
    if (priceEl && price) priceEl.textContent = price;

    var vEl = card.querySelector('.llm-ro__variant');
    var vName = opt.textContent.replace(/\s*—\s*out of stock\s*$/i, '').trim();
    if (vEl) vEl.textContent = vName;

    /* Rebuild the frequencies for THIS variant. "a:name|b:name" — the label is sanitised of '|'
       in the Liquid that writes it, so splitting is safe. */
    var freq = card.querySelector('[data-llm-ro-freq]');
    var as = card.querySelector('[data-llm-ro-as]');
    if (!freq) return;

    var raw = opt.getAttribute('data-plans') || '';
    var plans = raw ? raw.split('|').filter(Boolean) : [];

    freq.innerHTML = '';
    plans.forEach(function (p) {
      var i = p.indexOf(':');
      if (i < 1) return;
      var o = document.createElement('option');
      o.value = p.slice(0, i);
      o.textContent = p.slice(i + 1);
      freq.appendChild(o);
    });

    /* ⛔ If the newly chosen variant has NO selling plan, the Autoship tier must disappear — not
       sit there with an empty dropdown and a live Start button that would 422 on add. Hiding the
       whole block is also the honest answer: for that variant, Autoship genuinely is not on offer.
       The "Or" divider goes with it — a rule labelled "Or" with nothing after it is worse than no
       rule at all. */
    var gone = plans.length === 0;
    if (as) as.hidden = gone;
    var or = card.querySelector('[data-llm-ro-or]');
    if (or) or.hidden = gone;
  }

  document.addEventListener('change', function (e) {
    var sel = e.target.closest ? e.target.closest('[data-llm-ro-variant]') : null;
    if (!sel) return;
    applyVariant(sel.closest('[data-llm-ro]'), sel.options[sel.selectedIndex]);
  });

  /* Keyboard: Escape closes the open card, matching the cancel modal's behaviour. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var openCard = document.querySelector('.llm-ro--open');
    if (openCard) close(openCard);
  });
})();
